import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  fetchPlatformAssistantConfiguration,
  fetchTenantAssistantConfiguration,
  fetchWorkspaceAssistantConfiguration,
  savePlatformAssistantConfiguration,
  saveTenantAssistantConfiguration,
  saveWorkspaceAssistantConfiguration,
} from '@/api/assistantConfiguration/assistantConfigurationApi';
import type {
  PlatformAssistantConfiguration,
  RevisionedPlatformAssistantConfiguration,
  RevisionedSave,
  WorkspaceAssistantConfiguration,
  WorkspaceAssistantOverride,
  WorkspaceOwnerAssistantConfigurationUpdate,
} from '@/api/assistantConfiguration/assistantConfigurationApi.types';
import {
  ASSISTANT_PREFERENCES_QUERY_KEY,
  ASSISTANT_PULSE_QUERY_KEY,
  PLATFORM_ASSISTANT_CONFIGURATION_QUERY_KEY,
  TENANT_ASSISTANT_CONFIGURATION_QUERY_KEY,
  WORKSPACE_ASSISTANT_CONFIGURATION_QUERY_KEY,
  buildTenantAssistantConfigurationQueryKey,
} from '@/constants/queryKeys';

export function usePlatformAssistantConfigurationQuery(): UseQueryResult<RevisionedPlatformAssistantConfiguration> {
  return useQuery({
    queryKey: PLATFORM_ASSISTANT_CONFIGURATION_QUERY_KEY,
    queryFn: fetchPlatformAssistantConfiguration,
  });
}

export function useSavePlatformAssistantConfigurationMutation(): UseMutationResult<
  RevisionedPlatformAssistantConfiguration,
  unknown,
  RevisionedSave<PlatformAssistantConfiguration>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: savePlatformAssistantConfiguration,
    onSuccess: function storeSaved(saved) {
      queryClient.setQueryData(PLATFORM_ASSISTANT_CONFIGURATION_QUERY_KEY, saved);
      void queryClient.invalidateQueries({ queryKey: TENANT_ASSISTANT_CONFIGURATION_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: WORKSPACE_ASSISTANT_CONFIGURATION_QUERY_KEY });
    },
    onError: function refreshAfterConflict() {
      void queryClient.invalidateQueries({ queryKey: PLATFORM_ASSISTANT_CONFIGURATION_QUERY_KEY });
    },
  });
}

export function useTenantAssistantConfigurationQuery(
  tenantId: string
): UseQueryResult<WorkspaceAssistantConfiguration> {
  return useQuery({
    queryKey: buildTenantAssistantConfigurationQueryKey(tenantId),
    queryFn: function loadTenantConfiguration() {
      return fetchTenantAssistantConfiguration(tenantId);
    },
    enabled: tenantId.length > 0,
  });
}

export function useSaveTenantAssistantConfigurationMutation(
  tenantId: string
): UseMutationResult<
  WorkspaceAssistantConfiguration,
  unknown,
  RevisionedSave<WorkspaceAssistantOverride>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function saveTenantConfiguration(save: RevisionedSave<WorkspaceAssistantOverride>) {
      return saveTenantAssistantConfiguration(tenantId, save);
    },
    onSuccess: function storeSaved(saved) {
      queryClient.setQueryData(buildTenantAssistantConfigurationQueryKey(tenantId), saved);
    },
    onError: function refreshAfterConflict() {
      void queryClient.invalidateQueries({
        queryKey: buildTenantAssistantConfigurationQueryKey(tenantId),
      });
    },
  });
}

export function useWorkspaceAssistantConfigurationQuery(
  isEnabled: boolean
): UseQueryResult<WorkspaceAssistantConfiguration> {
  return useQuery({
    queryKey: WORKSPACE_ASSISTANT_CONFIGURATION_QUERY_KEY,
    queryFn: fetchWorkspaceAssistantConfiguration,
    enabled: isEnabled,
  });
}

export function useSaveWorkspaceAssistantConfigurationMutation(): UseMutationResult<
  WorkspaceAssistantConfiguration,
  unknown,
  RevisionedSave<WorkspaceOwnerAssistantConfigurationUpdate>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveWorkspaceAssistantConfiguration,
    onSuccess: function storeSaved(saved) {
      queryClient.setQueryData(WORKSPACE_ASSISTANT_CONFIGURATION_QUERY_KEY, saved);
      void queryClient.invalidateQueries({ queryKey: ASSISTANT_PULSE_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ASSISTANT_PREFERENCES_QUERY_KEY });
    },
    onError: function refreshAfterRefusal() {
      void queryClient.invalidateQueries({ queryKey: WORKSPACE_ASSISTANT_CONFIGURATION_QUERY_KEY });
    },
  });
}
